import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { inject, Injectable, Signal } from '@angular/core';

/**
 * interfaces
 */
import { ILookupItem } from '@core/services';
import { IFacilityDto, IUpdateFacilityPayload } from '../interfaces';

/**
 * services
 */
import { FacilityApiService } from './api';
import { LookupService } from '@core/services/lookup';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Business/Facade layer for the Facility sub-module.
 * Strips the IResponse wrapper from API responses and exposes
 * clean observable streams of domain data to components.
 * Also exposes the facility types lookup via the global LookupService.
 */
@Injectable()
export class FacilityService {

    private readonly _lookup = inject(LookupService);

    /**
     * Facility types lookup signal — sourced from the global LookupService.
     */
    public readonly facilityTypesLookup: Signal<ILookupItem<undefined>[]> = toSignal(this._lookup.facilityTypesLookup$, { initialValue: [] });

    /**
     * @param _api The Facility HTTP API service.
     */
    constructor(private readonly _api: FacilityApiService) {}

    /**
     * Fetches the facility details and unwraps the API envelope.
     * @returns Observable emitting the facility DTO.
     */
    public getFacility(): Observable<IFacilityDto> {
        return this._api.getFacility().pipe(
            map((response) => response.data),
        );
    }

    /**
     * Updates the facility and unwraps the API envelope.
     * @param id - The UUID of the facility to update.
     * @param payload - The update payload.
     * @returns Observable emitting the success message string.
     */
    public updateFacility(id: string, payload: IUpdateFacilityPayload): Observable<string> {
        return this._api.updateFacility(id, payload).pipe(
            map((response) => response.data),
        );
    }

}
